import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, doc, getDoc, collection, query, where, getDocs, onSnapshot
} from '../../firebase/config';
import BookingCalendarModal from '../../components/BookingCalendarModal';
import {
  ChevronLeft, Star, MapPin, User, Mail, Phone, Clock,
  Scissors, Sparkles, Droplets, Eye, Heart, Briefcase,
  MessageCircle, Share2, Award, Calendar, Users,
  Instagram, Globe, ChevronRight, BadgeCheck,
  Store, Car, Package, Check, Info
} from 'lucide-react';

// ---------- Category icons ----------
const getSpecializationIcon = (spec, size = 20) => {
  const s = (spec || '').toLowerCase();
  if (s.includes('lash')) return <Eye size={size} className="text-[#EFA9B6]" />;
  if (s.includes('nail')) return <Sparkles size={size} className="text-[#EFA9B6]" />;
  if (s.includes('hair')) return <Scissors size={size} className="text-[#EFA9B6]" />;
  if (s.includes('skin') || s.includes('esthetician')) return <Droplets size={size} className="text-[#EFA9B6]" />;
  if (s.includes('spa') || s.includes('massage')) return <Heart size={size} className="text-[#EFA9B6]" />;
  return <Briefcase size={size} className="text-[#EFA9B6]" />;
};

const ProfessionalClientView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceIdParam = searchParams.get('service');

  const { user } = useAuth();

  const [professional, setProfessional] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('services');
  const [selectedService, setSelectedService] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // ---------- Load pro ----------
  useEffect(() => {
    if (!id) return;

    const loadPro = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', id));
        if (!snap.exists()) {
          setError('Professional not found');
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };

        // FIX: Only show actual pros (allows pros currently in Client Mode to view their own page)
        const isActuallyAPro = 
          data.role === 'professional' || 
          data.userType === 'professional' ||
          data.businessName || 
          (data.specialization && data.specialization.length > 0);

        if (!isActuallyAPro) {
          setError('Not a professional account');
          setLoading(false);
          return;
        }

        setProfessional(data);
      } catch (err) {
        console.error('Failed to load pro:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadPro();
  }, [id]);

  // ---------- Load services ----------
  useEffect(() => {
    if (!id) return;

    const loadServices = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'services'), where('professionalId', '==', id))
        );
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.active !== false);
        setServices(data);
      } catch (err) {
        console.error('Failed to load services:', err);
      }
    };

    loadServices();
  }, [id]);

  // ---------- Load reviews (real-time) ----------
  useEffect(() => {
    if (!id) return;

    const q = query(
      collection(db, 'reviews'),
      where('professionalId', '==', id)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setReviews(data);
    }, (err) => {
      console.error('Reviews snapshot error:', err);
    });

    return () => unsub();
  }, [id]);

  // ---------- Auto-open service if ?service= is in URL ----------
  useEffect(() => {
    if (serviceIdParam && services.length > 0) {
      const target = services.find(s => s.id === serviceIdParam);
      if (target) {
        setSelectedService(target);
        setShowBookingModal(true);
        // Clear the param so refresh doesn't re-trigger
        setSearchParams({}, { replace: true });
      }
    }
  }, [serviceIdParam, services, setSearchParams]);

  // ---------- Derived: rating ----------
  const ratingData = useMemo(() => {
    if (reviews.length === 0) {
      return {
        average: Number(professional?.rating || 0),
        count: Number(professional?.ratingCount || 0),
        breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      };
    }
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let sum = 0;
    reviews.forEach(r => {
      const stars = Math.round(Number(r.rating) || 0);
      if (stars >= 1 && stars <= 5) breakdown[stars] += 1;
      sum += Number(r.rating) || 0;
    });
    return {
      average: reviews.length > 0 ? sum / reviews.length : 0,
      count: reviews.length,
      breakdown,
    };
  }, [reviews, professional]);

  // ---------- Helpers ----------
  const getCover = (service) => {
    if (Array.isArray(service.media) && service.media.length > 0) return service.media[0];
    if (service.media?.url) return service.media;
    if (service.mediaLegacy?.url) return service.mediaLegacy;
    return null;
  };

  const getPriceRange = (service) => {
    if (Array.isArray(service.options) && service.options.length > 0) {
      const prices = service.options.map(o => Number(o.price) || 0);
      return { from: Math.min(...prices), to: Math.max(...prices) };
    }
    return { from: Number(service.price) || 0, to: Number(service.price) || 0 };
  };

  const getLocationModeLabel = (service) => {
    const mode = service.locationMode || 'salon';
    if (mode === 'mobile') return 'Pro travels to you';
    if (mode === 'both') return 'Salon or mobile';
    return 'At the pro\'s place';
  };

  const getLocationIcon = (service) => {
    const mode = service.locationMode || 'salon';
    if (mode === 'mobile') return <Car size={11} />;
    if (mode === 'both') return <MapPin size={11} />;
    return <Store size={11} />;
  };

  const handleBookService = (service) => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Guard: Prevent booking yourself
    if (user.uid === id) {
      alert("You cannot book your own service.");
      return;
    }
    setSelectedService(service);
    setShowBookingModal(true);
  };

  const handleMessage = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Guard: Prevent messaging yourself
    if (user.uid === id) {
      alert("You cannot message yourself.");
      return;
    }
    navigate(`/dashboard/chat?with=${id}`);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: professional?.businessName || professional?.name,
          text: `Check out ${professional?.businessName || professional?.name} on Skytone`,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied');
      } catch {
        // silent
      }
    }
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !professional) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
          <User size={32} className="text-[#EFA9B6]" />
        </div>
        <h2 className="text-xl font-serif font-bold text-gray-900 mb-2">
          {error || 'Professional not found'}
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          This profile may have been removed or is unavailable.
        </p>
        <button
          onClick={() => navigate('/dashboard/search')}
          className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
        >
          Discover other professionals
        </button>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  const coverUrl = professional.coverImage || professional.profileImage;
  const avatarUrl = professional.profileImage;
  const displayName = professional.businessName || professional.name || 'Professional';
  const isClientViewing = user?.uid !== id;
  const socialLinks = professional.socialLinks || {};

  const tabs = [
    { id: 'services', label: 'Services', count: services.length },
    { id: 'about', label: 'About' },
    { id: 'reviews', label: 'Reviews', count: ratingData.count },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="max-w-4xl mx-auto pb-24">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 px-1"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back</span>
      </button>

      {/* Cover */}
      <div className="relative w-full h-48 sm:h-64 rounded-3xl overflow-hidden bg-[#F4EEFF] mb-4 border border-gray-100">
        {coverUrl ? (
          <img src={coverUrl} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {getSpecializationIcon(professional.specialization, 48)}
          </div>
        )}

        {/* Share button */}
        <button
          onClick={handleShare}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-gray-700 hover:bg-white shadow-sm"
          title="Share"
        >
          <Share2 size={16} />
        </button>
      </div>

      {/* Identity */}
      <div className="px-1 sm:px-4 -mt-16 sm:-mt-20 relative z-10 mb-6">
        <div className="flex items-end gap-4 mb-4">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] overflow-hidden border-4 border-white shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <User size={40} />
            )}
          </div>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 truncate">
                {displayName}
              </h1>
              <BadgeCheck size={20} className="text-[#EFA9B6] shrink-0" />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {professional.specialization || 'Beauty Professional'}
            </p>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
              {ratingData.average > 0 && (
                <span className="flex items-center gap-1">
                  <Star size={14} className="fill-amber-400 text-amber-400" />
                  <span className="font-bold text-gray-900">{ratingData.average.toFixed(1)}</span>
                  <span className="text-gray-400">({ratingData.count})</span>
                </span>
              )}
              {professional.location && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {professional.location}
                </span>
              )}
            </div>
          </div>

          {isClientViewing && (
            <button
              onClick={handleMessage}
              className="px-4 py-2.5 rounded-full bg-[#FCF8F9] text-gray-700 text-sm font-bold hover:bg-gray-100 transition-colors flex items-center gap-2 border border-gray-100 shrink-0"
            >
              <MessageCircle size={14} />
              Message
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-1 sm:px-4 mb-4">
        <div className="flex gap-1 p-1 bg-white border border-gray-100 rounded-full overflow-x-auto hide-scrollbar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#010101] text-white'
                  : 'text-gray-500 hover:bg-[#FCF8F9]'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`ml-1.5 text-[10px] ${
                  activeTab === tab.id ? 'text-white/70' : 'text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-1 sm:px-4">

        {/* ==================== SERVICES ==================== */}
        {activeTab === 'services' && (
          <>
            {services.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
                <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
                  {getSpecializationIcon(professional.specialization, 32)}
                </div>
                <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
                  No services listed yet
                </h3>
                <p className="text-gray-400 text-sm">
                  Check back soon or message {displayName.split(' ')[0]} for details.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {services.map(service => {
                  const cover = getCover(service);
                  const priceRange = getPriceRange(service);
                  const hasOptions = Array.isArray(service.options) && service.options.length > 0;
                  const mediaCount = Array.isArray(service.media) ? service.media.length : (cover ? 1 : 0);

                  return (
                    <div
                      key={service.id}
                      className="bg-white rounded-3xl border border-gray-100 hover:border-[#EFA9B6] transition-colors p-4"
                    >
                      <div className="flex gap-4">
                        {/* Thumbnail */}
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-[#FCF8F9] flex items-center justify-center shrink-0 relative">
                          {cover ? (
                            cover.type === 'video' ? (
                              <div className="w-full h-full flex items-center justify-center">
                                {getSpecializationIcon(service.specialization, 28)}
                              </div>
                            ) : (
                              <img src={cover.url} alt={service.title} className="w-full h-full object-cover" />
                            )
                          ) : (
                            getSpecializationIcon(service.specialization, 28)
                          )}
                          {mediaCount > 1 && (
                            <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                              +{mediaCount - 1}
                            </span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {service.category && (
                            <span className="text-[10px] font-bold tracking-wider text-[#EFA9B6] uppercase">
                              {service.category}
                            </span>
                          )}
                          <h3 className="font-bold text-gray-900 text-[15px] truncate">
                            {service.title}
                          </h3>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock size={11} />
                              {service.duration || '45 min'}
                            </span>
                            <span className="flex items-center gap-1">
                              {getLocationIcon(service)}
                              {getLocationModeLabel(service)}
                            </span>
                            {hasOptions && (
                              <span className="flex items-center gap-1 text-[#AF8BD4] font-bold">
                                {service.options.length} options
                              </span>
                            )}
                          </div>

                          {service.description && (
                            <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">
                              {service.description}
                            </p>
                          )}

                          {/* Price + Book */}
                          <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-50">
                            <div>
                              {hasOptions && priceRange.from !== priceRange.to ? (
                                <>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">From</p>
                                  <p className="font-bold text-gray-900">
                                    R{priceRange.from.toFixed(0)}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Price</p>
                                  <p className="font-bold text-gray-900">
                                    {service.variablePrice ? 'From ' : ''}R{priceRange.from.toFixed(0)}
                                  </p>
                                </>
                              )}
                            </div>
                            {isClientViewing && (
                              <button
                                onClick={() => handleBookService(service)}
                                className="px-5 py-2.5 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors flex items-center gap-1.5 shrink-0"
                              >
                                Book
                                <ChevronRight size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ==================== ABOUT ==================== */}
        {activeTab === 'about' && (
          <div className="space-y-4">
            {/* Bio */}
            <div className="bg-white rounded-3xl border border-gray-100 p-5">
              <h2 className="text-lg font-serif font-bold text-gray-900 mb-3">
                About {displayName.split(' ')[0]}
              </h2>
              {professional.bio ? (
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {professional.bio}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  No bio added yet.
                </p>
              )}
            </div>

            {/* Booking Policy */}
            {professional.bookingPolicy && (
              <div className="bg-white rounded-3xl border border-gray-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Info size={18} className="text-[#EFA9B6]" />
                  <h2 className="text-lg font-serif font-bold text-gray-900">
                    Booking Policy
                  </h2>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                  {professional.bookingPolicy}
                </p>
              </div>
            )}

            {/* Details grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {professional.specialization && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center shrink-0">
                    {getSpecializationIcon(professional.specialization, 18)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Specialization
                    </p>
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {professional.specialization}
                    </p>
                  </div>
                </div>
              )}

              {professional.location && (
                <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Location
                    </p>
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {professional.location}
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <Calendar size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Member Since
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {professional.createdAt?.toDate
                      ? professional.createdAt.toDate().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
                      : 'Recently'}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <Award size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Services
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {services.length} offered
                  </p>
                </div>
              </div>
            </div>

            {/* Quick stats */}
            {(ratingData.count > 0 || professional.ratingCount > 0) && (
              <div className="bg-white rounded-3xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Track Record</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-serif font-bold text-gray-900">
                      {ratingData.average.toFixed(1)}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          size={12}
                          className={n <= Math.round(ratingData.average)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-200'}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {ratingData.count} review{ratingData.count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-serif font-bold text-gray-900">
                      {services.length}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Service{services.length === 1 ? '' : 's'} available
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== REVIEWS ==================== */}
        {activeTab === 'reviews' && (
          <>
            {/* Rating summary */}
            {ratingData.count > 0 ? (
              <div className="bg-white rounded-3xl border border-gray-100 p-5 mb-4">
                <div className="flex items-start gap-6">
                  <div className="text-center shrink-0">
                    <p className="text-4xl font-serif font-bold text-gray-900">
                      {ratingData.average.toFixed(1)}
                    </p>
                    <div className="flex items-center gap-0.5 mt-1 justify-center">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star
                          key={n}
                          size={12}
                          className={n <= Math.round(ratingData.average)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-200'}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {ratingData.count} review{ratingData.count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    {[5, 4, 3, 2, 1].map(stars => {
                      const count = ratingData.breakdown[stars] || 0;
                      const percent = ratingData.count > 0 ? (count / ratingData.count) * 100 : 0;
                      return (
                        <div key={stars} className="flex items-center gap-2 mb-1.5">
                          <span className="text-[11px] font-bold text-gray-500 w-3">
                            {stars}
                          </span>
                          <Star size={9} className="fill-amber-400 text-amber-400 shrink-0" />
                          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-400 transition-all"
                              style={{ width: `${percent}%` }}
                            ></div>
                          </div>
                          <span className="text-[11px] text-gray-400 w-6 text-right">
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Review list */}
            {reviews.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
                <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star size={32} className="text-[#AF8BD4]" />
                </div>
                <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
                  No reviews yet
                </h3>
                <p className="text-gray-400 text-sm max-w-xs mx-auto">
                  Be the first to book {displayName.split(' ')[0]} and share your experience.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {reviews.map(review => (
                  <div
                    key={review.id}
                    className="bg-white rounded-3xl border border-gray-100 p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] overflow-hidden shrink-0">
                        {review.clientImage ? (
                          <img src={review.clientImage} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User size={18} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0">
                            <p className="font-bold text-sm text-gray-900 truncate">
                              {review.clientName || 'Client'}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {review.createdAt?.toDate
                                ? review.createdAt.toDate().toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })
                                : 'Recently'}
                            </p>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star
                                key={n}
                                size={11}
                                className={n <= review.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-gray-200'}
                              />
                            ))}
                          </div>
                        </div>

                        {review.text && (
                          <p className="text-sm text-gray-600 leading-relaxed mt-1">
                            {review.text}
                          </p>
                        )}

                        {review.serviceTitle && (
                          <p className="text-[11px] text-gray-400 mt-2">
                            Service: {review.serviceTitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ==================== CONTACT ==================== */}
        {activeTab === 'contact' && (
          <div className="space-y-3">
            {professional.phone && (
              <a
                href={`tel:${professional.phone}`}
                className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 hover:border-[#EFA9B6] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <Phone size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Phone</p>
                  <p className="text-sm font-bold text-gray-900">{professional.phone}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 shrink-0" />
              </a>
            )}

            {professional.email && (
              <a
                href={`mailto:${professional.email}`}
                className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 hover:border-[#EFA9B6] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <Mail size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{professional.email}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 shrink-0" />
              </a>
            )}

            {/* Social Links */}
            {socialLinks.whatsapp && (
              <a
                href={`https://wa.me/${socialLinks.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 hover:border-[#25D366] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#E8F8EE] flex items-center justify-center text-[#25D366] shrink-0">
                  <MessageCircle size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">WhatsApp</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{socialLinks.whatsapp}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 shrink-0" />
              </a>
            )}

            {socialLinks.instagram && (
              <a
                href={`https://instagram.com/${socialLinks.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 hover:border-[#E1306C] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#FFF0F5] flex items-center justify-center text-[#E1306C] shrink-0">
                  <Instagram size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Instagram</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{socialLinks.instagram}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 shrink-0" />
              </a>
            )}

            {socialLinks.tiktok && (
              <a
                href={`https://tiktok.com/@${socialLinks.tiktok.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 hover:border-[#000000] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-900 shrink-0">
                  <Globe size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">TikTok</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{socialLinks.tiktok}</p>
                </div>
                <ChevronRight size={16} className="text-gray-300 shrink-0" />
              </a>
            )}

            {professional.location && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <MapPin size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Location</p>
                  <p className="text-sm font-bold text-gray-900">{professional.location}</p>
                  {professional.address?.line1 && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {professional.address.line1}, {professional.address.city}
                    </p>
                  )}
                </div>
              </div>
            )}

            {professional.workingHours && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                    <Clock size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      Working Hours
                    </p>
                    <p className="text-sm font-bold text-gray-900">Weekly schedule</p>
                  </div>
                </div>
                <div className="space-y-1.5 pl-1">
                  {[
                    { id: 'mon', label: 'Mon' },
                    { id: 'tue', label: 'Tue' },
                    { id: 'wed', label: 'Wed' },
                    { id: 'thu', label: 'Thu' },
                    { id: 'fri', label: 'Fri' },
                    { id: 'sat', label: 'Sat' },
                    { id: 'sun', label: 'Sun' },
                  ].map(day => {
                    const d = professional.workingHours[day.id];
                    return (
                      <div key={day.id} className="flex justify-between text-xs">
                        <span className="text-gray-500 font-medium">{day.label}</span>
                        {d?.enabled ? (
                          <span className="font-bold text-gray-900">
                            {d.open} – {d.close}
                          </span>
                        ) : (
                          <span className="text-gray-400">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {professional.payshapId && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
                  <Check size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    Accepted Payments
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    PayShap · Cash · Card
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom sticky CTA (mobile) */}
      {isClientViewing && services.length > 0 && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-md lg:hidden pointer-events-none">
          <div className="bg-white/90 backdrop-blur-xl rounded-full shadow-[0_8px_32px_rgba(0,0,0,0.15)] border border-white/40 p-2 pointer-events-auto flex items-center gap-2">
            <button
              onClick={handleMessage}
              className="w-11 h-11 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
              title="Message"
            >
              <MessageCircle size={18} />
            </button>
            <button
              onClick={() => {
                setActiveTab('services');
                if (services.length > 0) {
                  handleBookService(services[0]);
                }
              }}
              className="flex-1 bg-[#010101] text-white rounded-full py-3 font-bold text-sm hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
            >
              Book a service
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Booking modal */}
      {showBookingModal && selectedService && (
        <BookingCalendarModal
          professional={professional}
          service={selectedService}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedService(null);
          }}
        />
      )}
    </div>
  );
};

export default ProfessionalClientView;